import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Stack,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Tabs,
  Tab,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Inventory as ProductIcon,
  MiscellaneousServices as ServiceIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { API_BASE_URL } from '../../config/api';
import { getAutoToken } from '../../utils/authUtils';
import logger from '../../utils/logger';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '18px',
    maxWidth: '940px',
    width: '100%',
    maxHeight: '92vh',
    overflow: 'hidden',
    boxShadow: '0 24px 80px rgba(0,0,0,0.18)'
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '22px 28px',
  background: 'linear-gradient(135deg, #111827 0%, #1f2937 100%)',
  borderBottom: '1px solid rgba(255,255,255,0.08)',
  '& .MuiTypography-root': {
    fontFamily: "'Inter', sans-serif",
    fontWeight: 600,
    fontSize: '22px',
    color: '#ffffff'
  },
  '& .MuiIconButton-root': {
    color: '#ffffff'
  }
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: '32px',
  backgroundColor: '#f8fafc'
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: '20px 32px',
  borderTop: '1px solid #e0e0e0',
  gap: '12px'
}));

const UploadBox = styled(Box)(({ theme }) => ({
  border: '2px dashed #d1d5db',
  borderRadius: '12px',
  padding: '32px',
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  backgroundColor: '#ffffff',
  '&:hover': {
    borderColor: '#1a1a1a',
    backgroundColor: '#f9f9f9'
  }
}));

const ImagePreview = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '150px',
  borderRadius: '8px',
  overflow: 'hidden',
  position: 'relative',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  }
}));

const ItemCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 16px rgba(0,0,0,0.12)'
  }
}));

const AddItemCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  border: '2px dashed #d1d5db',
  boxShadow: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '280px',
  transition: 'all 0.2s ease',
  backgroundColor: '#ffffff',
  '&:hover': {
    borderColor: '#1a1a1a',
    backgroundColor: '#f9f9f9'
  }
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  marginBottom: '24px',
  backgroundColor: '#ffffff',
  border: '1px solid #e5e7eb',
  borderRadius: '10px',
  padding: '4px',
  minHeight: '44px',
  '& .MuiTabs-indicator': {
    backgroundColor: 'transparent'
  }
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  fontFamily: "'Inter', sans-serif",
  fontWeight: 500,
  fontSize: '14px',
  textTransform: 'none',
  color: '#666666',
  minHeight: '36px',
  borderRadius: '8px',
  margin: '0 2px',
  '&.Mui-selected': {
    color: '#111827',
    backgroundColor: '#f1f5f9'
  }
}));

const ActionButton = styled(Button)(({ theme }) => ({
  fontFamily: "'Inter', sans-serif",
  fontWeight: 500,
  textTransform: 'none',
  borderRadius: '8px',
  padding: '10px 24px'
}));

const ProductServicePopup = ({ open, onClose, onSubmit, template, profile, initialProducts = [], initialServices = [] }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [suggestedProducts, setSuggestedProducts] = useState([]);
  const [suggestedServices, setSuggestedServices] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newItem, setNewItem] = useState({ name: '', description: '', image: null, imagePreview: null });
  const fileInputRef = useRef(null);
  const chooserFileInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [imageChooserOpen, setImageChooserOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [imageTarget, setImageTarget] = useState(null); // { type: 'product'|'service', id }

  const extractCategoryFromProfile = (p) => {
    const loc = p?.locationData || {};
    return (
      loc.primaryCategory?.displayName ||
      loc.primaryCategory?.name ||
      loc.categories?.primaryCategory?.displayName ||
      loc.categories?.primaryCategory?.name ||
      ''
    );
  };

  const profileName = profile?.name || profile?.locationData?.title || '';
  const displayCategory = extractCategoryFromProfile(profile);

  useEffect(() => {
    if (!open) return;
    setProducts(Array.isArray(initialProducts) ? initialProducts : []);
    setServices(Array.isArray(initialServices) ? initialServices : []);
    setActiveTab(0);
    setIsAddingNew(false);
    setNewItem({ name: '', description: '', image: null, imagePreview: null });
  }, [open, initialProducts, initialServices]);

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (!open) return;

      const category = extractCategoryFromProfile(profile);
      if (!category) {
        setSuggestedProducts([]);
        setSuggestedServices([]);
        return;
      }

      try {
        setSuggestionsLoading(true);
        setSuggestionsError(null);

        const token = getAutoToken();
        const businessName = profile?.name || profile?.locationData?.title || '';
        const city = profile?.locationData?.storefrontAddress?.locality || '';

        const res = await fetch(`${API_BASE_URL}/customer/ai/suggest-products-services`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          body: JSON.stringify({
            category,
            businessName,
            city,
            existingProducts: products.map(p => p.name),
            existingServices: services.map(s => s.name)
          })
        });

        const json = await res.json().catch(() => ({}));
        if (!res.ok || json?.success === false) {
          throw new Error(json?.message || 'Failed to load suggestions');
        }

        setSuggestedProducts(Array.isArray(json?.data?.products) ? json.data.products : []);
        setSuggestedServices(Array.isArray(json?.data?.services) ? json.data.services : []);
      } catch (e) {
        logger.warn('[FreeWebsite] Suggestion fetch failed:', e);
        setSuggestionsError(e.message || 'Failed to load suggestions');
        setSuggestedProducts([]);
        setSuggestedServices([]);
      } finally {
        setSuggestionsLoading(false);
      }
    };

    fetchSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, profile?.id]);

  const isSuggestionSelected = (name, type) => {
    const lower = String(name || '').trim().toLowerCase();
    if (!lower) return false;
    const list = type === 'product' ? products : services;
    return list.some(i => String(i.name || '').trim().toLowerCase() === lower);
  };

  const toggleSuggestedItem = (name, type) => {
    const trimmed = String(name || '').trim();
    if (!trimmed) return;
    const lower = trimmed.toLowerCase();

    if (type === 'product') {
      const exists = products.some(p => String(p.name || '').trim().toLowerCase() === lower);
      if (exists) {
        setProducts(prev => prev.filter(p => String(p.name || '').trim().toLowerCase() !== lower));
        return;
      }

      const id = Date.now() + Math.random();
      const item = {
        id,
        name: trimmed,
        description: '',
        image: '/images/placeholder.jpg'
      };

      setProducts(prev => [...prev, item]);
      setImageTarget({ type: 'product', id });
      setImageChooserOpen(true);
    } else {
      const exists = services.some(s => String(s.name || '').trim().toLowerCase() === lower);
      if (exists) {
        setServices(prev => prev.filter(s => String(s.name || '').trim().toLowerCase() !== lower));
        return;
      }

      const id = Date.now() + Math.random();
      const item = {
        id,
        name: trimmed,
        description: '',
        image: '/images/placeholder.jpg'
      };

      setServices(prev => [...prev, item]);
      setImageTarget({ type: 'service', id });
      setImageChooserOpen(true);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setIsAddingNew(false);
    setNewItem({ name: '', description: '', image: null, imagePreview: null });
  };

  const updateItemImage = (type, id, imageUrl) => {
    if (!id) return;
    if (type === 'product') {
      setProducts(prev => prev.map(p => (p.id === id ? { ...p, image: imageUrl } : p)));
    } else {
      setServices(prev => prev.map(s => (s.id === id ? { ...s, image: imageUrl } : s)));
    }
  };

  const handleExistingImageClick = (item, type) => {
    setImageTarget({ type, id: item.id });
    setImageChooserOpen(true);
  };

  const stopCameraStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  const openCamera = async () => {
    try {
      setCameraError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setCameraOpen(true);
    } catch (e) {
      logger.warn('[FreeWebsite] Camera open failed:', e);
      setCameraError('Camera not available or permission denied.');
      setCameraOpen(true);
    }
  };

  const captureFromCamera = () => {
    const video = videoRef.current;
    if (!video || !imageTarget) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    updateItemImage(imageTarget.type, imageTarget.id, dataUrl);
    stopCameraStream();
    setCameraOpen(false);
    setImageChooserOpen(false);
    setImageTarget(null);
  };

  const handleChooserUploadClick = () => {
    if (chooserFileInputRef.current) {
      chooserFileInputRef.current.value = null;
      chooserFileInputRef.current.click();
    }
  };

  const handleChooserFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file || !imageTarget) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target?.result;
      if (typeof dataUrl === 'string') {
        updateItemImage(imageTarget.type, imageTarget.id, dataUrl);
      }
      setImageChooserOpen(false);
      setImageTarget(null);
    };
    reader.readAsDataURL(file);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setNewItem(prev => ({
          ...prev,
          image: file,
          imagePreview: event.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddItem = () => {
    if (!newItem.name.trim()) return;

    const item = {
      id: Date.now(),
      name: newItem.name,
      description: newItem.description,
      image: newItem.imagePreview || '/images/placeholder.jpg'
    };

    if (activeTab === 0) {
      setProducts(prev => [...prev, item]);
    } else {
      setServices(prev => [...prev, item]);
    }

    setNewItem({ name: '', description: '', image: null, imagePreview: null });
    setIsAddingNew(false);
  };

  const handleDeleteItem = (id, type) => {
    if (type === 'product') {
      setProducts(prev => prev.filter(item => item.id !== id));
    } else {
      setServices(prev => prev.filter(item => item.id !== id));
    }
  };

  const handleSubmit = () => {
    onSubmit({ products, services });
  };

  const handleSkip = () => {
    onSubmit({ products: [], services: [] });
  };

  const currentItems = activeTab === 0 ? products : services;
  const itemType = activeTab === 0 ? 'product' : 'service';

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <StyledDialogTitle>
        <Stack spacing={0.25}>
          <Typography>Products & Services</Typography>
          <Typography variant="body2" sx={{ opacity: 0.8, fontWeight: 400 }}>
            {displayCategory ? `Suggested from ${displayCategory}` : 'Add what you offer'}{profileName ? ` • ${profileName}` : ''}
          </Typography>
        </Stack>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <StyledDialogContent>
        <Typography
          variant="body2"
          sx={{ color: '#475569', mb: 2.5, fontFamily: "'Inter', sans-serif", lineHeight: 1.6 }}
        >
          Add the key products and services you offer. These will appear on your website and help customers understand your business.
        </Typography>

        <StyledTabs value={activeTab} onChange={handleTabChange}>
          <StyledTab
            icon={<ProductIcon sx={{ fontSize: 20 }} />}
            iconPosition="start"
            label={`Products (${products.length})`}
          />
          <StyledTab
            icon={<ServiceIcon sx={{ fontSize: 20 }} />}
            iconPosition="start"
            label={`Services (${services.length})`}
          />
        </StyledTabs>

        <Box sx={{ mb: 3, p: 2.25, bgcolor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '12px' }}>
          {suggestionsLoading && (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.5 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" sx={{ color: '#666' }}>
                Loading suggested {activeTab === 0 ? 'products' : 'services'}...
              </Typography>
            </Stack>
          )}

          {!suggestionsLoading && suggestionsError && (
            <Alert severity="info" sx={{ borderRadius: '8px' }}>
              {suggestionsError}. You can still add items manually.
            </Alert>
          )}

          {!suggestionsLoading && !suggestionsError && (
            (activeTab === 0 ? suggestedProducts : suggestedServices).length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1a1a1a' }}>
                  Suggested {activeTab === 0 ? 'Products' : 'Services'}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {(activeTab === 0 ? suggestedProducts : suggestedServices).map((s, idx) => {
                    const type = activeTab === 0 ? 'product' : 'service';
                    const selected = isSuggestionSelected(s, type);
                    return (
                      <Chip
                        key={`${s}-${idx}`}
                        label={s}
                        icon={selected ? <CheckIcon sx={{ color: '#fff !important' }} /> : undefined}
                        variant={selected ? 'filled' : 'outlined'}
                        onClick={() => toggleSuggestedItem(s, type)}
                        sx={{
                          bgcolor: selected ? '#1976d2' : '#fff',
                          color: selected ? '#fff' : '#111827',
                          borderColor: selected ? '#1976d2' : '#e0e0e0',
                          cursor: 'pointer',
                          fontWeight: selected ? 600 : 500,
                          '&:hover': { bgcolor: selected ? '#1565c0' : '#f5f5f5' }
                        }}
                      />
                    );
                  })}
                </Stack>
              </Box>
            )
          )}
        </Box>

        <Grid container spacing={3}>
          {currentItems.map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item.id}>
              <ItemCard>
                <CardMedia
                  component="img"
                  height="150"
                  image={item.image}
                  alt={item.name}
                  onClick={() => handleExistingImageClick(item, itemType)}
                  sx={{ objectFit: 'cover', cursor: 'pointer' }}
                />
                <CardContent sx={{ pb: 1 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, fontFamily: "'Inter', sans-serif", mb: 0.5 }}
                  >
                    {item.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: '#666', fontSize: '13px', lineHeight: 1.5 }}
                    noWrap
                  >
                    {item.description}
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteItem(item.id, itemType)}
                    sx={{ color: '#d32f2f' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </CardActions>
              </ItemCard>
            </Grid>
          ))}

          <Grid item xs={12} sm={6} md={4}>
            {isAddingNew ? (
              <ItemCard sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleImageUpload}
                  />

                  {newItem.imagePreview ? (
                    <ImagePreview onClick={() => fileInputRef.current?.click()}>
                      <img src={newItem.imagePreview} alt="Preview" />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          bgcolor: 'rgba(0,0,0,0.4)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: 0,
                          transition: 'opacity 0.2s',
                          cursor: 'pointer',
                          '&:hover': { opacity: 1 }
                        }}
                      >
                        <Typography sx={{ color: '#fff', fontSize: '14px' }}>
                          Change Image
                        </Typography>
                      </Box>
                    </ImagePreview>
                  ) : (
                    <UploadBox onClick={() => fileInputRef.current?.click()}>
                      <CloudUploadIcon sx={{ fontSize: 40, color: '#9ca3af', mb: 1 }} />
                      <Typography variant="body2" sx={{ color: '#666' }}>
                        Click to upload image
                      </Typography>
                    </UploadBox>
                  )}

                  <TextField
                    size="small"
                    placeholder={`${activeTab === 0 ? 'Product' : 'Service'} Name`}
                    value={newItem.name}
                    onChange={(e) => setNewItem(prev => ({ ...prev, name: e.target.value }))}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        fontSize: '14px'
                      }
                    }}
                  />

                  <TextField
                    size="small"
                    placeholder="Description (optional)"
                    value={newItem.description}
                    onChange={(e) => setNewItem(prev => ({ ...prev, description: e.target.value }))}
                    fullWidth
                    multiline
                    rows={2}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        fontSize: '14px'
                      }
                    }}
                  />

                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleAddItem}
                      disabled={!newItem.name.trim()}
                      sx={{
                        flex: 1,
                        bgcolor: '#1a1a1a',
                        textTransform: 'none',
                        borderRadius: '8px',
                        '&:hover': { bgcolor: '#333' }
                      }}
                    >
                      Add
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setIsAddingNew(false);
                        setNewItem({ name: '', description: '', image: null, imagePreview: null });
                      }}
                      sx={{
                        flex: 1,
                        borderColor: '#d1d5db',
                        color: '#666',
                        textTransform: 'none',
                        borderRadius: '8px'
                      }}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Stack>
              </ItemCard>
            ) : (
              <AddItemCard onClick={() => setIsAddingNew(true)}>
                <Stack alignItems="center" spacing={1}>
                  <AddIcon sx={{ fontSize: 40, color: '#9ca3af' }} />
                  <Typography sx={{ color: '#666', fontSize: '14px' }}>
                    Add {activeTab === 0 ? 'Product' : 'Service'}
                  </Typography>
                </Stack>
              </AddItemCard>
            )}
          </Grid>
        </Grid>

        {(products.length > 0 || services.length > 0) && (
          <Box sx={{ mt: 3, p: 2, bgcolor: '#fff', borderRadius: '12px', border: '1px solid #e0e0e0' }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#1a1a1a' }}>
              Summary
            </Typography>
            <Stack direction="row" spacing={2}>
              {products.length > 0 && (
                <Chip
                  icon={<ProductIcon sx={{ fontSize: 18 }} />}
                  label={`${products.length} Product${products.length > 1 ? 's' : ''}`}
                  sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }}
                />
              )}
              {services.length > 0 && (
                <Chip
                  icon={<ServiceIcon sx={{ fontSize: 18 }} />}
                  label={`${services.length} Service${services.length > 1 ? 's' : ''}`}
                  sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }}
                />
              )}
            </Stack>
          </Box>
        )}
      </StyledDialogContent>

      <StyledDialogActions>
        <ActionButton
          variant="outlined"
          onClick={handleSkip}
          data-tutorial="skip-services"
          sx={{ borderColor: '#d1d5db', color: '#666' }}
        >
          Skip for Now
        </ActionButton>
        <ActionButton
          variant="contained"
          onClick={handleSubmit}
          sx={{ bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' } }}
        >
          Continue with Template
        </ActionButton>
      </StyledDialogActions>

      {/* Hidden file input for existing-item upload */}
      <input
        ref={chooserFileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleChooserFileChange}
      />

      {/* Image chooser dialog */}
      <Dialog
        open={imageChooserOpen}
        onClose={() => {
          setImageChooserOpen(false);
          setImageTarget(null);
        }}
        PaperProps={{ sx: { borderRadius: '12px', p: 0.5, minWidth: 320 } }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: 18 }}>Add / Update Photo</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
            Choose how you want to add an image for this {itemType}.
          </Typography>
          <Stack spacing={1.5}>
            <Button
              variant="contained"
              onClick={() => {
                setImageChooserOpen(false);
                openCamera();
              }}
              sx={{ bgcolor: '#111827', '&:hover': { bgcolor: '#0b1220' }, textTransform: 'none', borderRadius: '10px' }}
            >
              Camera
            </Button>
            <Button
              variant="outlined"
              onClick={handleChooserUploadClick}
              sx={{ textTransform: 'none', borderRadius: '10px' }}
            >
              Upload from Device
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Camera capture dialog */}
      <Dialog
        open={cameraOpen}
        onClose={() => {
          stopCameraStream();
          setCameraOpen(false);
          setImageTarget(null);
        }}
        PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden', width: 520, maxWidth: '95vw' } }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: 18 }}>Capture Photo</DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {cameraError ? (
            <Alert severity="warning" sx={{ m: 2, borderRadius: '10px' }}>
              {cameraError}
            </Alert>
          ) : (
            <Box sx={{ position: 'relative', bgcolor: '#000' }}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1.5 }}>
          <Button
            onClick={() => {
              stopCameraStream();
              setCameraOpen(false);
              setImageTarget(null);
            }}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          {!cameraError && (
            <Button
              variant="contained"
              onClick={captureFromCamera}
              sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' }, textTransform: 'none' }}
            >
              Capture
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </StyledDialog>
  );
};

export default ProductServicePopup;
